import CloseIcon from '@mui/icons-material/Close'
import FilterListIcon from '@mui/icons-material/FilterList'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import { Box, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material'
import { MRT_TableInstance } from 'material-react-table'
import { ChangeEvent, ReactNode, useCallback } from 'react'

import { MrtMobileCardConfig } from './mrtTypes.ts'

interface Props<T extends Record<string, any>> {
  title?: ReactNode | string
  config: MrtMobileCardConfig<T>
  table: MRT_TableInstance<T>
  activeFiltersCount: number
  selectedCount: number
  isFetching: boolean
  refetch: () => Promise<any>
  onOpenFilters: () => void
  customActions?: ReactNode
}

export function MrtMobileToolbar<T extends Record<string, any>>({
  title,
  config,
  table,
  activeFiltersCount,
  selectedCount,
  isFetching,
  refetch,
  onOpenFilters,
  customActions,
}: Props<T>) {
  // Extraes el estado directamente de la tabla
  const { globalFilter } = table.getState()

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      table.setGlobalFilter(event.target.value)
    },
    [table],
  )

  const handleClearSearch = useCallback(() => {
    table.setGlobalFilter('')
  }, [table])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        mb: 1,
      }}
    >
      {/* TITLE */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        {title && (
          <Typography variant="h6" fontWeight={600} noWrap>
            {title}
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ml: 'auto',
          }}
        >
          {selectedCount > 0 && (
            <Typography variant="caption" color="primary" sx={{ mr: 1 }}>
              {selectedCount} seleccionados
            </Typography>
          )}

          <Tooltip title="Actualizar">
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  void refetch()
                }}
                disabled={isFetching}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Filtros">
            <IconButton size="small" onClick={onOpenFilters}>
              <FilterListIcon fontSize="small" />

              {activeFiltersCount > 0 && (
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    minWidth: 15,
                    height: 15,
                    px: 0.25,
                    borderRadius: '50%',
                    backgroundColor: 'error.main',
                    color: 'error.contrastText',
                    fontSize: 9,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {activeFiltersCount}
                </Box>
              )}
            </IconButton>
          </Tooltip>

          {customActions}
        </Box>
      </Box>

      {/* SEARCH */}
      {config.enableSearch && (
        <TextField
          fullWidth
          size="small"
          value={globalFilter}
          placeholder={config.searchPlaceholder ?? 'Buscar...'}
          onChange={handleSearchChange}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: globalFilter ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      )}
    </Box>
  )
}
